<template>
  <div class="points-manage-container">

    <!-- 顶部积分概览卡片 -->
    <div class="dashboard-card">
      <!-- 骨架屏：积分概览加载中 -->
      <template v-if="isPointsLoading">
        <el-skeleton animated class="dashboard-skeleton">
          <template #template>
            <!-- 左侧：等级与总积分骨架 -->
            <div class="dashboard-left">
              <div class="plan-badge-bg"></div>
              <div class="plan-badge-content">
                <div class="plan-name">
                  <el-skeleton-item variant="text" class="sk-plan-name" />
                </div>
                <div class="total-points-display">
                  <div class="label">
                    <el-skeleton-item variant="text" class="sk-label" />
                  </div>
                  <div class="value">
                    <el-skeleton-item variant="text" class="sk-value" />
                  </div>
                </div>
              </div>
            </div>

            <!-- 右侧：竖排积分明细与进度条骨架 -->
            <div class="dashboard-right">
              <div class="progress-list">
                <div v-for="n in 3" :key="n" class="progress-row">
                  <div class="row-label">
                    <el-skeleton-item variant="text" class="sk-row-label" />
                  </div>
                  <div class="row-bar-area">
                    <span class="val-text">
                      <el-skeleton-item variant="text" class="sk-val-text" />
                    </span>
                    <el-skeleton-item variant="text" class="sk-progress-bar" />
                  </div>
                </div>
              </div>
            </div>
          </template>
        </el-skeleton>
      </template>
      <!-- 实际内容 -->
      <template v-else>
        <!-- 左侧：等级与总积分 -->
        <div class="dashboard-left">
          <div class="plan-badge-bg"></div>
          <div class="plan-badge-content">
            <div class="plan-name">{{ pointsData.verName }} <el-icon><StarFilled /></el-icon></div>
            <div class="total-points-display">
              <div class="label">{{ $t('views.account_center.total_points') }}</div>
              <div class="value">{{ formatNumber(pointsData.totalPoints) }}</div>
            </div>
          </div>
        </div>

        <!-- 右侧：竖排积分明细与进度条 -->
        <div class="dashboard-right">
          <!-- 进度条列表 -->
          <div class="progress-list">
            <!-- 订阅积分 -->
            <div class="progress-row">
              <div class="row-label">{{ $t('views.account_center.subscription_points') }}</div>
              <div class="row-bar-area">
                <span class="val-text">{{ formatNumber(pointsData.subPoints) }}/{{ formatNumber(pointsData.subTotal) }}</span>
                <div class="progress-bar">
                  <div class="progress-fill" :style="{ width: `${calcPercentage(pointsData.subPoints, pointsData.subTotal)}%` }" />
                </div>
              </div>
            </div>

            <!-- 增购积分 -->
            <div class="progress-row">
              <div class="row-label">
                {{ $t('views.account_center.purchased_points') }}
              </div>
              <div class="row-bar-area">
                <span class="val-text">{{ formatNumber(pointsData.buyPoints) }}/{{ formatNumber(pointsData.buyTotal) }}</span>
                <div class="progress-bar">
                  <div class="progress-fill" :style="{ width: `${calcPercentage(pointsData.buyPoints, pointsData.buyTotal)}%` }" />
                </div>
              </div>
            </div>

            <!-- 活动积分 -->
            <div class="progress-row">
              <div class="row-label">{{ $t('views.account_center.activity_points') }}</div>
              <div class="row-bar-area">
                <span class="val-text">{{ formatNumber(pointsData.actPoints) }}/{{ formatNumber(pointsData.actTotal) }}</span>
                <div class="progress-bar">
                  <div class="progress-fill" :style="{ width: `${calcPercentage(pointsData.actPoints, pointsData.actTotal)}%` }" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- 拓展包购买区域 (支持无限滚动加载) -->
    <div class="packages-section">
      <h3 class="section-title">{{ $t('views.account_center.buy_points_packages') }}</h3>

      <div class="packages-grid">
        <!-- 已加载的实际卡片内容 -->
        <div v-for="pkg in pointPackages" :key="pkg.id" class="package-card">
          <!-- 推荐标签 -->
          <div
            v-if="pkg.is_recommend === 1"
            class="popular-tag"
          >
            <span>{{ $t('views.account_center.recommended') }}</span>
          </div>
          <div class="pkg-header">
            <div class="pkg-icon"><el-icon><Coin /></el-icon></div>
            <div class="pkg-name">{{ pkg.package_name }}</div>
          </div>
          <div class="pkg-desc">{{ pkg.description }}</div>
          <div class="pkg-price-area">
            <span class="currency">{{ currencySymbol(pkg.currency) }}</span>
            <span class="price">{{ pkg.price }}</span>
            <span v-if="pkg.original_price" class="original-price">{{ formatPackagePrice(pkg.original_price, pkg.currency) }}</span>
          </div>
          <el-button class="apple-btn primary block" :loading="purchaseLoading === pkg.id" @click="handlePurchase(pkg)">
            {{ $t('views.account_center.buy_now') }}
          </el-button>
        </div>

        <!-- 骨架屏加载状态（首屏展现6个骨架，底部追加展现3个骨架） -->
        <template v-if="isLoading">
          <div v-for="n in (pointPackages.length === 0 ? 3 : 6)" :key="'sk-' + n" class="package-card skeleton-card">
            <el-skeleton animated class="package-skeleton">
              <template #template>
                <div class="pkg-header">
                  <div class="pkg-icon sk-pkg-icon">
                    <el-skeleton-item variant="rect" />
                  </div>
                  <div class="pkg-name">
                    <el-skeleton-item variant="text" class="sk-pkg-name" />
                  </div>
                </div>
                <div class="pkg-desc">
                  <el-skeleton-item variant="text" class="sk-pkg-desc" />
                </div>
                <div class="pkg-price-area">
                  <el-skeleton-item variant="text" class="sk-pkg-price" />
                </div>
                <el-skeleton-item variant="button" class="sk-pkg-btn apple-btn block" />
              </template>
            </el-skeleton>
          </div>
        </template>
      </div>
    </div>

  </div>
</template>

<script>
import { StarFilled, Coin } from '@element-plus/icons-vue'
import { getExpandPackageList, createExpandPackageOrder } from '@/api/team'
import { ElLoading } from 'element-plus'
import { formatCurrency, getCurrencySymbol } from '@/utils/currency'

export default {
  name: "PointsManage",
  components: { StarFilled, Coin },
  props: {
    pointsData: {
      type: Object,
      default: () => ({
        verName: '',
        totalPoints: 0,
        subPoints: 0,
        subTotal: 0,
        buyPoints: 0,
        buyTotal: 0,
        actPoints: 0,
        actTotal: 0
      })
    },
    isPointsLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // 拓展包列表
      pointPackages: [],
      // 分页信息
      pagination: {
        page: 1,
        pageSize: 6,
        total: 0
      },
      // 加载状态
      isLoading: false,
      purchaseLoading: null,
      // 支付相关
      loadingInstance: null,
      // 滚动容器
      scrollContainer: null
    }
  },
  computed: {
    // 是否已加载全部数据
    noMore() {
      return this.pointPackages.length >= this.pagination.total && this.pagination.total > 0;
    },
    // 是否禁用滚动加载（正在加载中，或是没有更多数据时禁用）
    scrollDisabled() {
      return this.isLoading || this.noMore;
    }
  },
  mounted() {
    // 延迟查找滚动容器，确保 DOM 已完全渲染
    this.$nextTick(() => {
      setTimeout(() => {
        this.findScrollContainer();
      }, 100);
    });
  },
  beforeUnmount() {
    // 移除滚动监听
    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener('scroll', this.handleScroll);
    }
  },
  methods: {
    currencySymbol(currency) {
      return getCurrencySymbol(currency)
    },
    formatPackagePrice(price, currency) {
      return formatCurrency(price, currency)
    },
    // 重置数据（切换tab时调用）
    resetData() {
      this.pointPackages = [];
      this.pagination.page = 1;
      this.pagination.total = 0;
      this.isLoading = false;
      this.purchaseLoading = null;
      // 移除旧的滚动监听
      if (this.scrollContainer) {
        this.scrollContainer.removeEventListener('scroll', this.handleScroll);
        this.scrollContainer = null;
      }
    },
    // 查找滚动容器
    findScrollContainer() {
      // 向上查找带有 overflow-y: auto 的父元素
      let parent = this.$el.parentElement;
      while (parent) {
        const style = window.getComputedStyle(parent);
        const overflowY = style.overflowY;

        // 检查是否有滚动能力（overflowY 为 auto/scroll 且内容高度大于可视高度）
        if ((overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight) {
          this.scrollContainer = parent;
          this.scrollContainer.addEventListener('scroll', this.handleScroll);
          return;
        }
        parent = parent.parentElement;
      }

      // 如果没有找到，尝试直接查找 .el-tabs__content
      const tabsContent = document.querySelector('.subscription-wrapper .el-tabs__content');
      if (tabsContent) {
        this.scrollContainer = tabsContent;
        this.scrollContainer.addEventListener('scroll', this.handleScroll);
        return;
      }

      // 如果还是没有找到，使用 window
      this.scrollContainer = window;
      window.addEventListener('scroll', this.handleScroll);
    },
    // 滚动事件处理
    handleScroll() {
      // 如果组件当前不可见（被隐藏），直接阻止后续逻辑
      if (this.$el && this.$el.offsetParent === null) return;

      if (this.scrollDisabled) return;

      let scrollTop, scrollHeight, clientHeight;

      if (this.scrollContainer === window) {
        scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        scrollHeight = document.documentElement.scrollHeight;
        clientHeight = document.documentElement.clientHeight;
      } else {
        scrollTop = this.scrollContainer.scrollTop;
        scrollHeight = this.scrollContainer.scrollHeight;
        clientHeight = this.scrollContainer.clientHeight;
      }

      // 距离底部 100px 时触发加载
      const distance = 100;
      if (scrollTop + clientHeight >= scrollHeight - distance) {
        this.loadMore();
      }
    },
    // 触发滚动加载更多
    loadMore() {
      if (this.scrollDisabled) return;
      this.pagination.page += 1;
      this.getExpandPackageList(true);
    },
    // 获取拓展包列表 - 由父组件在切换到积分管理tab时调用
    getExpandPackageList(isLoadMore = false) {
      // 首次加载(非追加)且已有缓存数据时，不再重复请求
      if (!isLoadMore && this.pointPackages.length > 0 && this.pagination.total > 0) {
        return
      }
      this.isLoading = true
      const params = {
        page: this.pagination.page,
        page_size: this.pagination.pageSize,
        is_pagination: 1
      }
      getExpandPackageList(params)
        .then(response => {
          if (response && response.data) {
            // 解析接口返回的数据结构
            // 接口返回: { code: 0, message: "ok", data: { current_page: 1, data: [...], total: 30, ... } }
            const resData = response.data;
            let list = [];
            let totalCount = 0;

            if (resData.data && Array.isArray(resData.data)) {
              // resData.data 是列表数据数组
              list = resData.data;
              totalCount = resData.total || 0;
            } else if (Array.isArray(resData)) {
              list = resData;
              totalCount = resData.length;
            }

            // 判断是追加数据还是覆盖数据
            if (isLoadMore) {
              this.pointPackages.push(...list);
            } else {
              this.pointPackages = list;
            }
            this.pagination.total = totalCount;

            // 数据加载后，重新检查滚动容器（因为内容高度可能变化了）
            this.$nextTick(() => {
              setTimeout(() => {
                this.checkAndRebindScrollContainer();
              }, 100);
            });
          }
        })
        .catch(err => {
          console.error(this.$t('views.account_center.get_package_list_failed'), err)
          if (!isLoadMore) {
            this.pointPackages = []
            this.pagination.total = 0
          }
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    // 检查并重新绑定滚动容器
    checkAndRebindScrollContainer() {
      // 如果已经有滚动容器，先检查它是否有滚动能力
      if (this.scrollContainer && this.scrollContainer !== window) {
        const hasScroll = this.scrollContainer.scrollHeight > this.scrollContainer.clientHeight;
        if (hasScroll) return;
      }

      // 重新查找有滚动能力的容器
      let parent = this.$el.parentElement;
      while (parent) {
        const style = window.getComputedStyle(parent);
        const overflowY = style.overflowY;
        const hasScroll = parent.scrollHeight > parent.clientHeight;

        if ((overflowY === 'auto' || overflowY === 'scroll') && hasScroll) {
          // 如果找到了新的容器，移除旧的监听
          if (this.scrollContainer && this.scrollContainer !== parent) {
            this.scrollContainer.removeEventListener('scroll', this.handleScroll);
          }
          this.scrollContainer = parent;
          this.scrollContainer.addEventListener('scroll', this.handleScroll);
          return;
        }
        parent = parent.parentElement;
      }
    },
    formatNumber(num) {
      if (!num && num !== 0) return '0'
      return num.toLocaleString()
    },
    // 安全计算百分比
    calcPercentage(val, total) {
      if (!total || total === 0) return 0
      return Math.min(100, Math.round((val / total) * 100))
    },
    // 购买拓展包
    handlePurchase(pkg) {
      this.purchaseLoading = pkg.id

      this.loadingInstance = ElLoading.service({
        lock: true,
        text: this.$t('views.account_center.processing_payment')
      })

      // 调用创建订单接口，传入 expand_id
      createExpandPackageOrder({ expand_id: pkg.id })
        .then(response => {
          if (response && response.data && response.data.checkout_url) {
            // 直接跳转到 checkout_url
            window.location.href = response.data.checkout_url
          } else {
            this.$message.error(this.$t('views.account_center.create_payment_session_failed'))
            this.loadingInstance.close()
            this.purchaseLoading = null
          }
        })
        .catch(error => {
          console.error(this.$t('views.account_center.payment_error'), error)
          this.$message.error(this.$t('views.account_center.payment_error'))
          this.loadingInstance.close()
          this.purchaseLoading = null
        })
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/css/views/account_center/components/points_manage.scss';
</style>
